import { useEffect, useRef, useState } from "react";
import { Platform } from "react-native";
import {
    AudioModule,
    RecordingPresets,
    requestRecordingPermissionsAsync,
    setAudioModeAsync,
} from "expo-audio";
import { useMaybeRoomContext } from "@livekit/components-react";
import { IS_WEB } from "../shared/platform";

// stop() only ends the capture: the native recorder keeps holding the mic until
// it is released, which on iOS pins the session to playAndRecord (quieter
// playback, mic indicator on).
const releaseRecorder = (recorder) => {
    try {
        recorder.remove();
    } catch (e) {
        // Already released — ignore.
    }
};

// A reconnecting room still owns the mic: opening a recorder of our own in
// that window would fight the call for it.
const IN_CALL_STATES = ["connected", "reconnecting", "signalReconnecting"];

/**
 * On mobile during a live, the level is LiveKit's audioLevel — exactly 0 as
 * soon as the student stops talking. The web keeps measuring its own stream.
 */
export const levelComesFromCall = (room) => !IS_WEB && IN_CALL_STATES.includes(room?.state);

/**
 * Hook to monitor microphone level in real-time
 * Works on both web (Web Audio API) and native (expo-audio).
 * During a connected live, the level is derived from LiveKit's own capture
 * instead of opening a second mic recorder, which would fight the call for
 * the audio session.
 *
 * `deviceId` (web, facultatif) : le micro à écouter. Sans lui, la mesure porte
 * toujours sur l'entrée par défaut du système — ce qui rendait le sélecteur du
 * test d'entrée illisible, puisque changer de micro ne changeait pas les
 * barres. Contrainte souhaitée et non stricte : un micro débranché entre-temps
 * doit faire retomber la mesure sur le défaut, pas la supprimer.
 */
export const useMicrophoneLevel = (permissionsGranted, deviceId) => {
    const [micLevel, setMicLevel] = useState(0);
    const micInterval = useRef(null);
    const webAudioContext = useRef(null);
    const webStream = useRef(null);
    const recordingRef = useRef(null);
    // Startup is async (permissions, prepare); a teardown can land mid-flight.
    // Every start claims a run id — if it is no longer the current one when an
    // await resolves, the start aborts instead of leaving a live mic behind.
    const runIdRef = useRef(0);
    // undefined outside any LiveKitRoom provider — never throws.
    const room = useMaybeRoomContext();
    const inCall = levelComesFromCall(room);

    const stopMicTest = async () => {
        runIdRef.current += 1;
        if (micInterval.current) {
            clearInterval(micInterval.current);
            micInterval.current = null;
        }
        if (IS_WEB) {
            if (webStream.current) {
                webStream.current.getTracks().forEach((track) => track.stop());
                webStream.current = null;
            }
            if (webAudioContext.current) {
                // Idempotent close — guard against `InvalidStateError:
                // Can't close an AudioContext twice` (P-1757, cluster #11).
                if (webAudioContext.current.state !== "closed") {
                    try {
                        await webAudioContext.current.close();
                    } catch (e) {
                        // Already transitioned, non-actionable.
                    }
                }
                webAudioContext.current = null;
            }
        } else {
            if (recordingRef.current) {
                const recorder = recordingRef.current;
                recordingRef.current = null;
                try {
                    await recorder.stop();
                } catch (e) {
                    // Recorder already stopped — ignore.
                }
                releaseRecorder(recorder);
            }
        }
        setMicLevel(0);
    };

    const startMicTest = async () => {
        const runId = ++runIdRef.current;
        const isStale = () => runId !== runIdRef.current;
        try {
            if (IS_WEB) {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: deviceId ? { deviceId } : true,
                });
                if (isStale()) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }
                webStream.current = stream;
                const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                webAudioContext.current = audioContext;
                const source = audioContext.createMediaStreamSource(stream);
                const analyser = audioContext.createAnalyser();
                analyser.fftSize = 2048;
                source.connect(analyser);
                const dataArray = new Uint8Array(analyser.fftSize);
                micInterval.current = setInterval(() => {
                    analyser.getByteTimeDomainData(dataArray);
                    let sum = 0;
                    for (let i = 0; i < dataArray.length; i++) {
                        const v = dataArray[i] - 128;
                        sum += v * v;
                    }
                    const rms = Math.sqrt(sum / dataArray.length);
                    setMicLevel(Math.min(1, rms / 30));
                }, 100);
            } else if (inCall) {
                // In-call (troubleshooting panel, device picker): read the level
                // LiveKit already computes for the local participant. audioLevel
                // is 0..1, low for normal speech — boost it to fill the meter.
                micInterval.current = setInterval(() => {
                    const level = room.localParticipant?.audioLevel ?? 0;
                    setMicLevel(Math.min(1, level * 4));
                }, 100);
            } else {
                const { granted } = await requestRecordingPermissionsAsync();
                if (!granted || isStale()) {
                    return;
                }
                // expo-audio's Android `setAudioModeAsync` resets the audio
                // manager to MODE_NORMAL + speakerphone on every call, whatever
                // keys are passed — overriding LiveKit's MODE_IN_COMMUNICATION,
                // which carries hardware echo cancellation, until the app
                // restarts. The recorder doesn't need it on Android, so this
                // hook never calls it there.
                if (Platform.OS !== "android") {
                    await setAudioModeAsync({
                        allowsRecording: true,
                        playsInSilentMode: true,
                    });
                    if (isStale()) return;
                }
                const meteringOptions = {
                    ...RecordingPresets.HIGH_QUALITY,
                    isMeteringEnabled: true,
                };
                const recorder = new AudioModule.AudioRecorder(meteringOptions);
                recordingRef.current = recorder;
                // Options must go through prepareToRecordAsync: its JS wrapper
                // flattens the per-platform sections into the native shape.
                await recorder.prepareToRecordAsync(meteringOptions);
                if (isStale()) {
                    recordingRef.current = null;
                    releaseRecorder(recorder);
                    return;
                }
                recorder.record();
                // expo-audio exposes no continuous status callback for metering,
                // so poll the recorder state to drive the live level meter.
                micInterval.current = setInterval(() => {
                    const status = recorder.getStatus();
                    if (status?.metering !== undefined) {
                        const level = Math.min(
                            1,
                            Math.pow(10, status.metering / 20) * 20
                        );
                        setMicLevel(level);
                    }
                }, 100);
            }
        } catch (error) {
            console.error("Microphone test error", error);
            await stopMicTest();
        }
    };

    // inCall is a dependency on purpose: when a live connects while the
    // meter is already running on its own recorder (mic test opened from the
    // pre-call screen), that recorder must be torn down and the level re-read
    // from LiveKit — otherwise a second mic capture fights the call for the
    // session for as long as the panel stays open.
    useEffect(() => {
        if (permissionsGranted?.microphone) {
            startMicTest();
            return () => {
                stopMicTest();
            };
        } else {
            stopMicTest();
        }
        // `deviceId` en dépendance : changer de micro doit rouvrir la mesure
        // sur le nouveau, sinon les barres continuent d'écouter l'ancien.
    }, [permissionsGranted?.microphone, deviceId, inCall]);

    return micLevel;
};
